import { debounce, capitalize } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, HideText, Icon } from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import {
  Actions,
  hasAccessTo,
  hasPermissionTo,
} from '../../Lib/Constants/roles.ts';
import ApplicationStatus from '../../Lib/Constants/vendor-application-status';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import { FilterSearch } from '../FilterSearch/FilterSearch.tsx';
import SocialMediaDataGrid from './SocialMediaDataGrid.tsx';
import {
  useArchiveInfluencer,
  useGetInfluencers,
} from '../../Lib/Hooks/vendor.hooks.ts';
import { ArchiveConfirmationModal } from '../ReusableComponents/NewDesign/Modals/ArchiveConfirmationModal.tsx';

export const filterMap = {
  all: {},
  pending: {
    rejection_reason: { value: null },
    submitted_at: { value: null },
    approved_at: { value: null },
    application_status: {
      value: 'Application Pending',
    },
    application_archived_at: { value: null },
  },
  pendingAccountingApproval: {
    submitted_at: { value: null, ne: true },
    approved_at: { value: null, ne: true },
    application_status: {
      value: 'Pending Accounting Approval',
    },
    application_archived_at: { value: null },
  },
  approved: {
    application_status: {
      value: capitalize(ApplicationStatus.APPROVED),
    },
    application_archived_at: { value: null },
  },
  rejected: {
    application_status: {
      value: capitalize(ApplicationStatus.REJECTED),
    },
    application_archived_at: { value: null },
  },
  archived: {
    application_archived_at: {
      value: null,
      ne: true,
    },
  },
};

const filterItems = [
  {
    icon: 'file/file-text',
    value: 'all',
    label: 'All',
  },
  {
    icon: 'three-dots-menu/three-dots-menu-horizontal-circle',
    value: 'pending',
    label: 'Incomplete',
  },
  {
    icon: 'circle-dot',
    value: 'pendingAccountingApproval',
    label: 'Pending Accounting Approval',
  },
  {
    icon: 'check/check-tick-circle',
    value: 'approved',
    label: 'Approved',
  },
  {
    icon: 'ban/ban-right',
    value: 'rejected',
    label: 'Rejected',
  },
  {
    icon: 'folder/folder-arrow-down',
    value: 'archived',
    label: 'Archived',
  },
];

export default function SocialMediaDashboard(props) {
  const { currentUser = {} } = props;
  const [params, setParams] = useState({
    ...decodeLocation(props.location.search),
    search: '',
    page: 1,
    limit: 30,
    sortBy: 'email',
    sortOrder: 'DESC',
    vendorFilters: 'all',
    invitedBy: '',
  });
  const [exporting, setExporting] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const { mutate: archiveInfluencer } = useArchiveInfluencer();

  const queryParams = {
    ...params,
    invitedBy: undefined,
    vendorFilters: {
      ...filterMap[params.vendorFilters],
      invited_by: params.invitedBy
        ? {
            value: params.invitedBy,
          }
        : undefined,
    },
  };

  const { data: influencers, isLoading } = useGetInfluencers(queryParams);

  useEffect(() => {
    props.getTeam();
  }, []);

  const canExport = useMemo(
    () =>
      hasPermissionTo(
        'social-media-console',
        currentUser.role,
        Actions.READ,
        'EXPORT',
      ),
    [currentUser],
  );

  return (
    <Page.Container>
      <Header.Major icon="camera" title="Influencers">
        {hasAccessTo(
          'social-media-console/influencer-init',
          currentUser.role,
        ) && (
          <Link to="/social-media-console/influencer-init">
            <Button size="sm">
              <Icon name="plus/plus-square" variant="solid" />
              <HideText notAbove="lg">Create{'\u00A0'}Application</HideText>
            </Button>
          </Link>
        )}
      </Header.Major>
      <Header.Minor
        filter={{
          value: params.vendorFilters,
          onChange: (value) =>
            setParams((p) => ({
              ...p,
              vendorFilters: value,
              page: 1,
            })),
          items: filterItems,
        }}
        search={{
          value: params.search,
          onChange: debounce(
            (value) => setParams((p) => ({ ...p, search: value })),
            500,
          ),
        }}
      >
        <FilterSearch
          withSearch
          value={params.invitedBy}
          items={[
            { value: '', label: 'Any' },
            ...props.team.map((item) => ({
              value: item.id,
              label: `${item.first_name} ${item.last_name}`,
            })),
          ]}
          label="Invited By"
          emptyLabel="Invited By"
          onAddValue={(value) =>
            setParams((p) => ({
              ...p,
              invitedBy: value,
            }))
          }
          onRemoveValue={() =>
            setParams((p) => ({
              ...p,
              invitedBy: '',
            }))
          }
        />
        {canExport && (
          <Button
            onClick={async () => {
              setExporting(true);
              props.exportInfluencers().finally(() => setExporting(false));
            }}
            loading={exporting}
            color="secondary"
            size="sm"
          >
            <Icon name="download/download-down" />
            <HideText notAbove="lg">Export</HideText>
          </Button>
        )}
      </Header.Minor>
      <Page.Content>
        <SocialMediaDataGrid
          currentUser={currentUser}
          rows={influencers?.rows || []}
          count={influencers?.count || 0}
          loading={isLoading}
          params={params}
          pageSizeOptions={[30]}
          paginationModel={{
            pageSize: 30,
            page: (params.page || 1) - 1,
          }}
          onPaginationModelChange={(model) => {
            setParams((p) => ({ ...p, page: model.page + 1 }));
          }}
          sortModel={
            params?.sortBy
              ? [
                  {
                    field: params.sortBy,
                    sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                  },
                ]
              : []
          }
          onSortModelChange={(model) => {
            const sortItem = model[0];
            setParams((p) => ({
              ...p,
              sortBy: sortItem?.field || undefined,
              sortOrder: sortItem?.sort.toUpperCase() || undefined,
            }));
          }}
          archiveInfluencer={(id, archive) => {
            setArchiveTarget({ id, archive });
          }}
        />
      </Page.Content>
      {archiveTarget && (
        <ArchiveConfirmationModal
          isOpen={!!archiveTarget}
          onClose={() => setArchiveTarget(null)}
          action={archiveTarget.archive ? 'archive' : 'unarchive'}
          onConfirm={async () => {
            await archiveInfluencer({
              id: archiveTarget.id,
              archive: archiveTarget.archive,
              params: {
                ...params,
                vendorFilters: filterMap[params.vendorFilters],
              },
            });
            setArchiveTarget(null);
          }}
        />
      )}
    </Page.Container>
  );
}

SocialMediaDashboard.propTypes = {
  currentUser: PropTypes.shape({
    role: PropTypes.string,
  }),
  exportInfluencers: PropTypes.func.isRequired,
  getTeam: PropTypes.func.isRequired,
  team: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  logout: PropTypes.func.isRequired,
};
